// ============================================================================
// KNOWLEDGE EXPLORER - "Ask the Knowledge Base" Testing Interface
// ============================================================================

import { useState } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useAIStore } from '@/stores/aiStore'
import {
  knowledgeApi,
  type KnowledgeQueryResult,
  type KnowledgeCategory,
} from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORY_LABELS: Record<KnowledgeCategory, string> = {
  framework: 'Framework',
  case_study: 'Case Study',
  best_practice: 'Best Practice',
  template: 'Template',
  research: 'Research',
  guide: 'Guide',
  book: 'Book',
  other: 'Other',
}

// ============================================================================
// FEEDBACK BUTTONS COMPONENT
// ============================================================================

interface FeedbackButtonsProps {
  chunkId: string
  query: string
  onFeedback: (chunkId: string, helpful: boolean, comment?: string) => void
  disabled?: boolean
}

function FeedbackButtons({ chunkId, query, onFeedback, disabled }: FeedbackButtonsProps) {
  const [feedbackGiven, setFeedbackGiven] = useState<boolean | null>(null)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')

  const handleFeedback = (helpful: boolean) => {
    if (helpful) {
      onFeedback(chunkId, true)
      setFeedbackGiven(true)
    } else {
      setShowComment(true)
    }
  }

  const submitNegativeFeedback = () => {
    onFeedback(chunkId, false, comment || undefined)
    setFeedbackGiven(false)
    setShowComment(false)
  }

  if (feedbackGiven !== null) {
    return (
      <span className="text-xs text-text-muted">
        {feedbackGiven ? 'Thanks!' : 'Thanks for the feedback'}
      </span>
    )
  }

  if (showComment) {
    return (
      <div className="flex items-center gap-2">
        <Input
          placeholder="What was wrong? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="h-7 text-xs w-48"
        />
        <Button size="sm" variant="outline" onClick={submitNegativeFeedback} className="h-7 text-xs">
          Submit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setShowComment(false)}
          className="h-7 text-xs"
        >
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs text-text-muted mr-1">Helpful?</span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => handleFeedback(true)}
        disabled={disabled}
        className="h-7 px-2 text-xs"
      >
        Yes
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => handleFeedback(false)}
        disabled={disabled}
        className="h-7 px-2 text-xs"
      >
        No
      </Button>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface KnowledgeExplorerProps {
  initialQuery?: string
}

export function KnowledgeExplorer({ initialQuery = '' }: KnowledgeExplorerProps) {
  const { accountId } = useSessionStore()
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<KnowledgeQueryResult[]>([])
  const [lastQuery, setLastQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [topK, setTopK] = useState(5)
  const [minSimilarity, setMinSimilarity] = useState(0.7)
  const [categoryFilter, setCategoryFilter] = useState<KnowledgeCategory | 'all'>('all')
  const [embeddingModel, setEmbeddingModel] = useState('')

  const handleSearch = async () => {
    if (!accountId) {
      setError('No account selected')
      return
    }

    if (!query.trim()) {
      setError('Please enter a question')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await knowledgeApi.query(accountId, {
        q: query.trim(),
        topK,
        minSimilarity,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
      })

      setResults(response.results)
      setLastQuery(response.query)
      setEmbeddingModel(response.model)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Query failed')
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const handleFeedback = async (chunkId: string, helpful: boolean, comment?: string) => {
    if (!accountId) return

    try {
      await knowledgeApi.submitFeedback(accountId, {
        chunkId,
        query: lastQuery,
        helpful,
        comment,
      })
    } catch (err) {
      console.error('Failed to submit feedback:', err)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSearch()
    }
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-display text-text">Ask the Knowledge Base</h1>
        <p className="text-text-secondary">
          Test your knowledge base by asking questions. Results show relevant chunks with source
          attribution and similarity scores.
        </p>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Query Input */}
            <div className="space-y-2">
              <Label htmlFor="query">Your Question</Label>
              <Textarea
                id="query"
                placeholder="e.g., What is the Jobs-to-be-Done framework?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
              />
            </div>

            {/* Options Row */}
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="topK">Results</Label>
                <Select value={String(topK)} onValueChange={(val) => setTopK(Number(val))}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Top 3</SelectItem>
                    <SelectItem value="5">Top 5</SelectItem>
                    <SelectItem value="10">Top 10</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="minSimilarity">Min Similarity</Label>
                <Select
                  value={String(minSimilarity)}
                  onValueChange={(val) => setMinSimilarity(Number(val))}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">50%</SelectItem>
                    <SelectItem value="0.6">60%</SelectItem>
                    <SelectItem value="0.7">70%</SelectItem>
                    <SelectItem value="0.8">80%</SelectItem>
                    <SelectItem value="0.9">90%</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={categoryFilter}
                  onValueChange={(val) => setCategoryFilter(val as KnowledgeCategory | 'all')}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleSearch} disabled={loading} className="ml-auto">
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {/* Error */}
            {error && <p className="text-sm text-error">{error}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-display text-text">
              Results for: &ldquo;{lastQuery}&rdquo;
            </h2>
            <span className="text-sm text-text-muted">
              {results.length} result{results.length !== 1 ? 's' : ''} found
              {embeddingModel && ` • Model: ${embeddingModel}`}
            </span>
          </div>

          {results.map((result, index) => (
            <Card key={result.chunkId}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-warm-400">#{index + 1}</span>
                      {result.sourceTitle}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      {result.sourceCategory && (
                        <Badge variant="outline">
                          {CATEGORY_LABELS[result.sourceCategory as KnowledgeCategory] ||
                            result.sourceCategory}
                        </Badge>
                      )}
                      {result.topic && (
                        <span className="text-xs text-text-muted">Topic: {result.topic}</span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        result.similarity >= 0.9
                          ? 'default'
                          : result.similarity >= 0.8
                          ? 'secondary'
                          : 'outline'
                      }
                    >
                      {(result.similarity * 100).toFixed(0)}% match
                    </Badge>
                    <FeedbackButtons
                      chunkId={result.chunkId}
                      query={lastQuery}
                      onFeedback={handleFeedback}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-warm-100 rounded-lg p-4 text-sm whitespace-pre-wrap text-text">
                  {result.content}
                </div>
                {result.sourceUrl && (
                  <a
                    href={result.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-business hover:underline mt-2 inline-block"
                  >
                    View source →
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && lastQuery && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-text-secondary">
              No results found for &ldquo;{lastQuery}&rdquo;
            </p>
            <p className="text-sm text-text-muted mt-2">
              Try lowering the minimum similarity threshold or using different keywords.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Initial State */}
      {!lastQuery && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-text-secondary">
              Enter a question above to search your knowledge base.
            </p>
            <p className="text-sm text-text-muted mt-2">
              The AI will find the most relevant content to help answer your question.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default KnowledgeExplorer
